import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    IoArrowBack, IoHeart, IoCart, IoStar, 
    IoAdd, IoRemove, IoCloudUpload, IoCheckmark, IoDocument, IoClose, IoOptions,
    IoWarning, IoCheckmarkCircle, IoAlert, IoImage
} from 'react-icons/io5';
import { useProduct, type Material, type Color, type Size, type CustomizationOption } from '@/hooks/useProducts';
import useCartStore from '@/stores/CartStore';
import useWishlistStore from '@/stores/WishlistStore';
import Button from '@/components/common/Button/Button';
import Spinner from '@/components/common/Spinner/Spinner';
import toast from 'react-hot-toast';
import './ProductDetail.css';

const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const productId = parseInt(id || '0');
    
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState<'description' | 'customization' | 'reviews'>('description');
    
    // Customization state
    const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
    const [selectedColor, setSelectedColor] = useState<Color | null>(null);
    const [selectedSize, setSelectedSize] = useState<Size | null>(null);
    const [selectedCustomizations, setSelectedCustomizations] = useState<CustomizationOption[]>([]);
    const [customText, setCustomText] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [patternFile, setPatternFile] = useState<File | null>(null);
    const [patternPreview, setPatternPreview] = useState<string | null>(null);
    const [specialNotes, setSpecialNotes] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [showCartPreview, setShowCartPreview] = useState(false);
    const [recentlyAddedItem, setRecentlyAddedItem] = useState<any>(null);
    
    const { data: product, isLoading, error } = useProduct(productId);
    const { addItem: addToCart, getItemCount, total_amount } = useCartStore();
    const { addItem: addToWishlist, removeItem: removeFromWishlist, hasItem: isInWishlist } = useWishlistStore();
    
    const cartItemCount = getItemCount();
    const cartTotal = total_amount;

    // Set initial quantity to minimum order quantity
    useEffect(() => {
        if (product?.min_order_quantity && quantity < product.min_order_quantity) {
            setQuantity(product.min_order_quantity);
        }
    }, [product?.min_order_quantity]);

    // Real-time validation
    useEffect(() => {
        const errors = validateCustomizations();
        setValidationErrors(errors);
    }, [selectedMaterial, selectedColor, selectedSize, quantity, product]);

    // File preview handlers
    const handleLogoUpload = (file: File | null) => {
        setLogoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setLogoPreview(null);
        }
    };

    const handlePatternUpload = (file: File | null) => {
        setPatternFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => setPatternPreview(e.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setPatternPreview(null);
        }
    };

    // Calculate price with customizations
    const calculatePrice = () => {
        if (!product) return 0;
        
        let price = product.base_price;
        
        // Add material cost
        if (selectedMaterial) {
            price += selectedMaterial.additional_cost;
        }
        
        // Add color cost
        if (selectedColor) {
            price += selectedColor.additional_cost;
        }
        
        // Add size cost
        if (selectedSize) {
            price += selectedSize.additional_cost;
        }
        
        // Add customization costs
        selectedCustomizations.forEach(customization => {
            price += customization.additional_cost;
        });
        
        return price;
    };

    const unitPrice = calculatePrice();
    const totalPrice = unitPrice * quantity;

    // Get bulk pricing discount
    const getBulkDiscount = () => {
        if (!product || !product.bulk_pricing || !product.bulk_pricing.length) return 0;
        
        const applicableTier = product.bulk_pricing
            .filter(tier => quantity >= tier.min_quantity && (!tier.max_quantity || quantity <= tier.max_quantity))
            .sort((a, b) => b.discount_percentage - a.discount_percentage)[0];
            
        return applicableTier ? applicableTier.discount_percentage : 0;
    };

    const bulkDiscount = getBulkDiscount();
    const discountedPrice = totalPrice * (1 - bulkDiscount / 100);

    const validateCustomizations = (): string[] => {
        const errors: string[] = [];
        
        // Check required material selection
        if (product?.available_materials?.length && !selectedMaterial) {
            errors.push('Por favor selecciona un material');
        }
        
        // Check required color selection
        if (product?.available_colors?.length && !selectedColor) {
            errors.push('Por favor selecciona un color');
        }
        
        // Check required size selection
        if (product?.available_sizes?.length && !selectedSize) {
            errors.push('Por favor selecciona un tamaño');
        }
        
        // All customizations are optional, so no validation needed
        
        // Check minimum quantity
        if (quantity < (product?.min_order_quantity || 1)) {
            errors.push(`La cantidad mínima de pedido es ${product?.min_order_quantity || 1}`);
        }
        
        return errors;
    };

    const handleAddToCart = async () => {
        if (!product || isAddingToCart) return;
        
        // Validate all customizations
        const errors = validateCustomizations();
        if (errors.length > 0) {
            toast.error('Por favor completa todas las selecciones requeridas:\n' + errors.join('\n'), {
                duration: 4000,
                icon: '⚠️'
            });
            return;
        }
        
        setIsAddingToCart(true);
        
        try {
            const price = calculatePrice();

            addToCart({
                product_id: product.id,
                product_name: product.name,
                product_image: product.main_image_url,
                base_price: product.base_price,
                quantity,
                unit_price: price,
                customization: {
                    custom_text: product.allows_text_customization ? customText : undefined,
                    custom_logo: product.allows_logo_upload ? logoFile?.name : undefined,
                    custom_pattern: product.allows_pattern_upload ? patternFile?.name : undefined,
                    notes: specialNotes || undefined,
                },
                material_name: selectedMaterial?.name,
                color_name: selectedColor?.name,
                color_hex: selectedColor?.hex_code,
                size_name: selectedSize?.name,
            });
            
            // Show success feedback
            toast.success(`¡${product.name} agregado al carrito exitosamente!`, {
                duration: 3000,
                icon: '🛒'
            });
            
            // Store recently added item for cart preview
            setRecentlyAddedItem({
                product_name: product.name,
                quantity,
                unit_price: price,
                total_price: bulkDiscount > 0 ? discountedPrice : totalPrice
            });
            
            // Show cart preview modal
            setShowCartPreview(true);
            
        } catch (error) {
            toast.error('Error al agregar el artículo al carrito. Por favor intenta de nuevo.', {
                duration: 4000,
                icon: '❌'
            });
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleWishlistToggle = () => {
        if (!product) return;
        
        if (isInWishlist(product.id)) {
            removeFromWishlist(product.id);
            toast.success('Eliminado de la lista de deseos', {
                duration: 2000,
                icon: '💔'
            });
        } else {
            addToWishlist({
                id: product.id,
                product_id: product.id,
                product_name: product.name,
                product_image: product.main_image_url,
                base_price: product.base_price,
                category_name: product.category?.name,
            });
            toast.success('Agregado a la lista de deseos', {
                duration: 2000,
                icon: '❤️'
            });
        }
    };

    // Calculate customization progress
    const getCustomizationProgress = () => {
        let completed = 0;
        let total = 0;
        
        // Required selections
        if (product?.available_materials?.length) {
            total++;
            if (selectedMaterial) completed++;
        }
        if (product?.available_colors?.length) {
            total++;
            if (selectedColor) completed++;
        }
        if (product?.available_sizes?.length) {
            total++;
            if (selectedSize) completed++;
        }
        
        return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 100 };
    };

const customizationProgress = getCustomizationProgress();

if (isLoading) {
    return (
        <div className="product_detail_container">
            <div className="product_detail_loading">
                <Spinner size="lg" />
                <p>Cargando detalles del producto...</p>
            </div>
        </div>
    );
}

if (error || !product) {
    return (
        <div className="product_detail_container">
            <div className="product_detail_error">
                <IoAlert size={48} color="#dc3545" />
                <h2>Producto No Encontrado</h2>
                <p>El producto que buscas no existe o ha sido eliminado.</p>
                <Button variant="primary" onClick={() => navigate('/products')}>
                    Ver Productos
                </Button>
            </div>
        </div>
    );
}

return (
    <div className="product_detail_container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
            <button onClick={() => navigate(-1)} className="back_button">
                <IoArrowBack size={20} />
                Volver
            </button>
            <span className="breadcrumb_separator">/</span>
            <span className="breadcrumb_item">{product.category?.name}</span>
            <span className="breadcrumb_separator">/</span>
            <span className="breadcrumb_current">{product.name}</span>
        </div>

        {/* Main Product Section */}
        <div className="product_detail_main">
                {/* Product Images */}
                <div className="product_images_section">
                    <div className="main_image_container">
                        <img 
                            src={product.gallery_images?.[selectedImageIndex] || product.main_image_url} 
                            alt={product.name}
                            className="main_product_image"
                        />
                        <button 
                            className={`wishlist_button ${isInWishlist(product.id) ? 'active' : ''}`}
                            onClick={handleWishlistToggle}
                        >
                            <IoHeart size={24} />
                        </button>
                    </div>
                    
                    {product.gallery_images && product.gallery_images.length > 1 && (
                        <div className="product_image_thumbnails">
                            {product.gallery_images.map((image, index) => (
                                <button
                                    key={index}
                                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                                    onClick={() => {
                                        if (product.gallery_images && product.gallery_images.length > index) {
                                            setSelectedImageIndex(index);
                                        }
                                    }}
                                >
                                    <img src={image} alt={`${product.name} ${index + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div className="product_info_section">
                    <div className="product_header">
                        <h1 className="product_title">{product.name}</h1>
                        <div className="product_rating">
                            <div className="rating_stars">
                                {[1, 2, 3, 4, 5].map(star => (
                                    <IoStar key={star} size={16} className="star" />
                                ))}
                            </div>
                            <span className="rating_text">4.8 (124 reseñas)</span>
                        </div>
                    </div>

                    <div className="product_price_section">
                        <div className="price_display">
                            <span className="unit_price">
                                ${unitPrice.toFixed(2)} cada uno
                            </span>
                            {bulkDiscount > 0 && (
                                <span className="bulk_discount">
                                    {bulkDiscount}% descuento por volumen aplicado
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="product_description">
                        <p>{product.short_description || product.description}</p>
                    </div>

                    {/* Customization Options */}
                    <div className="customization_section">
                        <div className="customization_header">
                            <h3 className="customization_title">
                                <IoOptions size={20} />
                                Personaliza Tu Producto
                            </h3>
                            {customizationProgress.total > 0 && (
                                <div className="customization_progress">
                                    <div className="progress_bar">
                                        <div 
                                            className="progress_fill" 
                                            style={{ width: `${customizationProgress.percentage}%` }}
                                        ></div>
                                    </div>
                                    <span className="progress_text">
                                        {customizationProgress.completed}/{customizationProgress.total} selecciones requeridas
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        {/* Validation Errors */}
                        {validationErrors.length > 0 && (
                            <div className="validation_errors">
                                <IoAlert size={16} />
                                <div className="error_list">
                                    {validationErrors.map((error, index) => (
                                        <div key={index} className="error_item">{error}</div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Materials */}
                        {product.available_materials && product.available_materials.length > 0 && (
                            <div className="customization_group">
                                <div className="group_header">
                                    <label className="group_label">
                                        <IoOptions size={16} />
                                        Material
                                    </label>
                                    <span className="group_required">Requerido</span>
                                    {selectedMaterial && (
                                        <IoCheckmarkCircle className="group_completed" size={16} />
                                    )}
                                </div>
                                <div className="options_grid">
                                    {product.available_materials.map((material: Material) => (
                                        <button
                                            key={material.id}
                                            className={`option_button ${selectedMaterial?.id === material.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedMaterial(material)}
                                        >
                                            <span className="option_name">{material.name}</span>
                                            {material.additional_cost > 0 && (
                                                <span className="option_cost">+${material.additional_cost}</span>
                                            )}
                                            {selectedMaterial?.id === material.id && (
                                                <IoCheckmark className="selected_icon" size={14} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Colors */}
                        {product.available_colors && product.available_colors.length > 0 && (
                            <div className="customization_group">
                                <div className="group_header">
                                    <label className="group_label">Color</label>
                                    <span className="group_required">Requerido</span>
                                    {selectedColor && (
                                        <IoCheckmarkCircle className="group_completed" size={16} />
                                    )}
                                </div>
                                <div className="color_options">
                                    {product.available_colors.map((color) => (
                                        <button
                                            key={color.id}
                                            className={`color_option ${selectedColor?.id === color.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedColor(color)}
                                            style={{ backgroundColor: color.hex_code }}
                                            title={`${color.name}${color.additional_cost > 0 ? ` (+$${color.additional_cost})` : ''}`}
                                        >
                                            {selectedColor?.id === color.id && (
                                                <IoCheckmark className="color_selected_icon" size={16} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <div className="selected_color_info">
                                    {selectedColor && (
                                        <span>Seleccionado: {selectedColor.name}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Sizes */}
                        {product.available_sizes && product.available_sizes.length > 0 && (
                            <div className="customization_group">
                                <div className="group_header">
                                    <label className="group_label">Tamaño</label>
                                    <span className="group_required">Requerido</span>
                                    {selectedSize && (
                                        <IoCheckmarkCircle className="group_completed" size={16} />
                                    )}
                                </div>
                                <div className="size_options">
                                    {product.available_sizes.map((size) => (
                                        <button
                                            key={size.id}
                                            className={`size_option ${selectedSize?.id === size.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            <div className="size_content">
                                                <span className="size_name">{size.name}</span>
                                                {size.additional_cost > 0 && (
                                                    <span className="size_cost">+${size.additional_cost}</span>
                                                )}
                                            </div>
                                            {selectedSize?.id === size.id && (
                                                <IoCheckmark className="selected_icon" size={16} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Custom Text */}
                        {product.allows_text_customization && (
                            <div className="customization_group">
                                <h4>
                                    <IoDocument />
                                    Texto Personalizado
                                    <span className="optional_indicator">(Opcional)</span>
                                </h4>
                                <div className="text_input_group">
                                    <textarea
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        placeholder="Ingresa tu texto personalizado aquí..."
                                        maxLength={product.max_text_length}
                                        className="custom_text_input"
                                    />
                                    <div className="text_counter">
                                        <span className={customText.length > product.max_text_length * 0.8 ? 'warning' : ''}>
                                            {customText.length}/{product.max_text_length} caracteres
                                        </span>
                                    </div>
                                    {customText && (
                                        <div className="text_preview">
                                            <strong>Vista previa:</strong> {customText}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* File Upload Options */}
                        <div className="upload_options">
                            {/* Logo Upload */}
                            {product.allows_logo_upload && (
                                <div className="upload_item">
                                    <div className="upload_header">
                                        <label className="upload_label">
                                            <IoCloudUpload size={20} />
                                            Subir Logo
                                        </label>
                                        <span className="upload_optional">(Opcional)</span>
                                    </div>
                                    <div className="upload_area">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleLogoUpload(e.target.files?.[0] || null)}
                                            className="file_input"
                                            id="logo-upload"
                                        />
                                        <label htmlFor="logo-upload" className="upload_dropzone">
                                            {logoPreview ? (
                                                <div className="image_preview">
                                                    <img src={logoPreview} alt="Logo preview" className="preview_image" />
                                                    <div className="preview_overlay">
                                                        <IoImage size={24} />
                                                        <span>Cambiar Logo</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="upload_placeholder">
                                                    <IoCloudUpload size={32} />
                                                    <span>Haz clic para subir logo</span>
                                                    <small>PNG, JPG hasta 5MB</small>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                    {logoFile && (
                                        <div className="file_info">
                                            <span className="file_name">{logoFile.name}</span>
                                            <button
                                                onClick={() => handleLogoUpload(null)}
                                                className="remove_file"
                                                type="button"
                                            >
                                                <IoClose size={14} />
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Pattern Upload */}
                            {product.allows_pattern_upload && (
                                <div className="upload_item">
                                    <div className="upload_header">
                                        <label className="upload_label">
                                            <IoCloudUpload size={20} />
                                            Subir Patrón
                                        </label>
                                        <span className="upload_optional">(Opcional)</span>
                                    </div>
                                    <div className="upload_area">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handlePatternUpload(e.target.files?.[0] || null)}
                                            className="file_input"
                                            id="pattern-upload"
                                        />
                                        <label htmlFor="pattern-upload" className="upload_dropzone">
                                            {patternPreview ? (
                                                <div className="image_preview">
                                                    <img src={patternPreview} alt="Pattern preview" className="preview_image" />
                                                    <div className="preview_overlay">
                                                        <IoImage size={24} />
                                                        <span>Cambiar Patrón</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="upload_placeholder">
                                                    <IoCloudUpload size={32} />
                                                    <span>Haz clic para subir patrón</span>
                                                    <small>PNG, JPG hasta 5MB</small>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                    {patternFile && (
                                        <div className="file_info">
                                            <span className="file_name">{patternFile.name}</span>
                                            <button
                                                onClick={() => handlePatternUpload(null)}
                                                className="remove_file"
                                                type="button"
                                            >
                                                <IoClose size={14} />
                                                Eliminar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Additional Customizations */}
                        {product.available_customizations && product.available_customizations.length > 0 && (
                            <div className="customization_group">
                                <div className="group_header">
                                    <label className="group_label">Opciones Premium</label>
                                    <span className="group_optional">Opcional</span>
                                </div>
                                <div className="additional_options">
                                    {product.available_customizations.map((option) => (
                                        <label key={option.id} className="checkbox_option">
                                            <input
                                                type="checkbox"
                                                checked={selectedCustomizations.some(c => c.id === option.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedCustomizations([...selectedCustomizations, option]);
                                                    } else {
                                                        setSelectedCustomizations(selectedCustomizations.filter(c => c.id !== option.id));
                                                    }
                                                }}
                                            />
                                            <span className="checkbox_label">
                                                {option.name}
                                                {option.additional_cost > 0 && (
                                                    <span className="option_cost">+${option.additional_cost}</span>
                                                )}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Special Notes */}
                        <div className="customization_group">
                            <h4>Notas Especiales</h4>
                            <textarea
                                value={specialNotes}
                                onChange={(e) => setSpecialNotes(e.target.value)}
                                placeholder="Cualquier instrucción especial o notas..."
                                className="special_notes_input"
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Quantity and Add to Cart */}
                    <div className="purchase_section">
                        <div className="quantity_selector">
                            <label>Cantidad:</label>
                            <div className="quantity_controls">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="quantity_btn"
                                >
                                    <IoRemove size={16} />
                                </button>
                                <span className="quantity_value">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="quantity_btn"
                                >
                                    <IoAdd size={16} />
                                </button>
                            </div>
                            <div className="quantity_info">
                                Mín: {product.min_order_quantity || 1}
                            </div>
                        </div>

                        <div className="total_price">
                            <span className="total_label">Total:</span>
                            <span className="total_value">
                                {bulkDiscount > 0 ? (
                                    <>
                                        <span className="original_total">${totalPrice.toFixed(2)}</span>
                                        <span className="discounted_total">${discountedPrice.toFixed(2)}</span>
                                    </>
                                ) : (
                                    <span className="final_total">${totalPrice.toFixed(2)}</span>
                                )}
                            </span>
                        </div>

                        <div className="cart_actions">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleAddToCart}
                                disabled={isAddingToCart || validationErrors.length > 0}
                            >
                                {isAddingToCart ? (
                                    <>
                                        <Spinner size="sm" />
                                        Agregando al Carrito...
                                    </>
                                ) : (
                                    <>
                                        <IoCart size={16} />
                                        Agregar al Carrito
                                        {validationErrors.length > 0 && (
                                            <IoWarning size={14} className="warning_icon" />
                                        )}
                                    </>
                                )}
                            </Button>
                            
                            <div className="secondary_actions">
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    onClick={async () => {
                                        await handleAddToCart();
                                        if (validationErrors.length === 0) {
                                            navigate('/checkout');
                                        }
                                    }}
                                    disabled={isAddingToCart || validationErrors.length > 0}
                                >
                                    Comprar Ahora
                                </Button>
                                
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    onClick={() => navigate('/cart')}
                                >
                                    Carrito ({cartItemCount})
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Details Tabs */}
            <div className="product_details_tabs">
                <div className="tab_headers">
                    <button
                        className={`tab_header ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        Descripción
                    </button>
                    <button
                        className={`tab_header ${activeTab === 'customization' ? 'active' : ''}`}
                        onClick={() => setActiveTab('customization')}
                    >
                        Opciones de Personalización
                    </button>
                    <button
                        className={`tab_header ${activeTab === 'reviews' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reviews')}
                    >
                        Reseñas (124)
                    </button>
                </div>

                <div className="tab_content">
                    {activeTab === 'description' && (
                        <div className="description_content">
                            <p>{product.description}</p>
                        </div>
                    )}

                    {activeTab === 'customization' && (
                        <div className="customization_content">
                            <h4>Opciones de Personalización Disponibles:</h4>
                            <div className="customization_details">
                                {/* Materials */}
                                {product.available_materials && product.available_materials.length > 0 && (
                                    <div className="tab_content_section">
                                        <h4>Materiales Disponibles</h4>
                                        <ul className="materials_list">
                                            {product.available_materials.map((material: Material) => (
                                                <li key={material.id}>
                                                    <strong>{material.name}</strong>
                                                    {material.description && <span> - {material.description}</span>}
                                                    {material.additional_cost > 0 && <span className="price_modifier"> (+${material.additional_cost})</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Colors */}
                                {product.available_colors && product.available_colors.length > 0 && (
                                    <div className="tab_content_section">
                                        <h4>Colores Disponibles</h4>
                                        <div className="colors_grid">
                                            {product.available_colors.map((color: Color) => (
                                                <div key={color.id} className="color_detail">
                                                    <div className="color_swatch" style={{ backgroundColor: color.hex_code }}></div>
                                                    <span className="color_detail_name">{color.name}</span>
                                                    {color.additional_cost > 0 && <span className="price_modifier">(+${color.additional_cost})</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sizes */}
                                {product.available_sizes && product.available_sizes.length > 0 && (
                                    <div className="tab_content_section">
                                        <h4>Tamaños Disponibles</h4>
                                        <ul className="sizes_list">
                                            {product.available_sizes.map((size: Size) => (
                                                <li key={size.id}>
                                                    <strong>{size.name}</strong>
                                                    {size.description && <span> - {size.description}</span>}
                                                    {size.additional_cost > 0 && <span className="price_modifier"> (+${size.additional_cost})</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <div className="reviews_content">
                            <div className="reviews_summary">
                                <div className="average_rating">
                                    <span className="rating_number">4.8</span>
                                    <div className="rating_stars">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <IoStar key={star} size={20} className="star" />
                                        ))}
                                    </div>
                                    <span className="review_count">Basado en 124 reseñas</span>
                                </div>
                            </div>
                            <div className="reviews_placeholder">
                                <p>La funcionalidad de reseñas se implementará en una actualización futura.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Cart Preview Modal */}
            {showCartPreview && recentlyAddedItem && (
                <div className="cart_preview_modal">
                    <div className="modal_backdrop" onClick={() => setShowCartPreview(false)}></div>
                    <div className="modal_content">
                        <div className="modal_header">
                            <h3>
                                <IoCheckmarkCircle size={24} className="success_icon" />
                                ¡Agregado al Carrito!
                            </h3>
                            <button 
                                onClick={() => setShowCartPreview(false)}
                                className="close_modal"
                            >
                                <IoClose size={20} />
                            </button>
                        </div>
                        
                        <div className="modal_body">
                            <div className="added_item_summary">
                                <img 
                                    src={product.main_image_url} 
                                    alt={product.name}
                                    className="item_image"
                                />
                                <div className="item_details">
                                    <h4>{recentlyAddedItem.product_name}</h4>
                                    <div className="item_specs">
                                        {selectedMaterial && (
                                            <span className="spec">Material: {selectedMaterial.name}</span>
                                        )}
                                        {selectedColor && (
                                            <span className="spec">Color: {selectedColor.name}</span>
                                        )}
                                        {selectedSize && (
                                            <span className="spec">Tamaño: {selectedSize.name}</span>
                                        )}
                                    </div>
                                    <div className="item_pricing">
                                        <span className="quantity">Cant: {recentlyAddedItem.quantity}</span>
                                        <span className="price">${recentlyAddedItem.total_price.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="cart_summary">
                                <div className="summary_row">
                                    <span>Total del Carrito:</span>
                                    <span>${cartTotal.toFixed(2)}</span>
                                </div>
                                <div className="summary_row">
                                    <span>Artículos en el Carrito:</span>
                                    <span>{cartItemCount}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal_actions">
                            <Button
                                variant="secondary"
                                onClick={() => setShowCartPreview(false)}
                                className="continue_shopping"
                            >
                                Seguir Comprando
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setShowCartPreview(false);
                                    navigate('/cart');
                                }}
                                className="view_cart"
                            >
                                <IoCart size={16} />
                                Ver Carrito y Finalizar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
